import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsGeneral';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import api from '../../../../../api';
import Modal from './UnidadesOrgModal';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import Tabs from './UnidadesOrgTabs';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';
import { isSessionSuperAdmin, getSessionEmpresaId } from '../../../../../utils/SessionUser';
import { useResourcePermissions } from '../../../../../utils/useResourcePermissions';

const PAGE_SIZE = 10;
const nombreTabla = "Unidades Organizacionales";
const endpointRead = (tipo) => ENDPOINTS.unidadesOrgSecurity(tipo);
const endpointWrite = ENDPOINTS.unidadesOrg;
const endpointById = ENDPOINTS.unidadesOrgById;
const modelo = Models.unidadesOrg;

const maps = {
  mapCreate: (f) => ({
    idZonaGeografica: Number(f.idZonaGeografica) || 0,
    idDireccion: Number(f.idDireccion) || 0,
    clave: f.clave,
    nombre: f.nombre,
    idTipoUnidadOrganizacional: Number(f.idTipoUnidadOrganizacional) || 0,
    descripcion: f.descripcion,
  }),
  mapUpdate: (id, f) => ({
    idUnidadOrganizacional: id,
    idZonaGeografica: Number(f.idZonaGeografica) || 0,
    idDireccion: Number(f.idDireccion) || 0,
    clave: f.clave,
    nombre: f.nombre,
    idTipoUnidadOrganizacional: Number(f.idTipoUnidadOrganizacional) || 0,
    descripcion: f.descripcion,
  }),
};

export default function Catalog() {
  const { puedeCrear, puedeEditar, puedeEliminar } = useResourcePermissions();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);
  const [page, setPage] = useState(1);
  const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });
  const [showChildData, setShowChildData] = useState(false);
  const sessionEmpresaId = getSessionEmpresaId();
  const isSuperAdmin = isSessionSuperAdmin();

  const triggerAlert = (message, variant = 'error', title = 'Error') => {
    setModalOpen(false);
    setDeleteOpen(false);
    setAlertConfig({ open: true, message, variant, title });
  };

  // 👉 catálogos para reemplazar IDs por valores
  const [zonas, setZonas] = useState([]);
  const [direcciones, setDirecciones] = useState([]);
  const [tiposUnidadOrg, setTiposUnidadOrg] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [empresasUnidadesOrg, setEmpresasUnidadesOrg] = useState([]);

  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, selectedId]);

  const loadData = useCallback(async (filterToUse) => {
    setLoading(true);
    try {
      const finalFilter = filterToUse || (showChildData ? 'hijas' : 'propias');
      const [resUnidades, resZonas, resDirecciones, resTipos, resEmpresas, resRel] = await Promise.all([
        api.get(endpointRead(finalFilter)),
        api.get(ENDPOINTS.zonasGeograficasSecurity(finalFilter)),
        api.get(ENDPOINTS.direcciones),
        api.get(ENDPOINTS.tipoUnidadOrg),
        api.get(ENDPOINTS.empresas),
        api.get(ENDPOINTS.empresasUnidadesOrg),
      ]);

      // Unidades organizacionales normalizadas
      setRows((resUnidades.data || []).map((x) => modelo(x)));

      // Zonas
      const rawZonas = Array.isArray(resZonas.data)
        ? resZonas.data
        : Array.isArray(resZonas.data?.data)
          ? resZonas.data.data
          : [];
      setZonas(rawZonas.map((z) => Models.zonasGeograficas(z)));

      // Direcciones
      const rawDirecciones = Array.isArray(resDirecciones.data)
        ? resDirecciones.data
        : Array.isArray(resDirecciones.data?.data)
          ? resDirecciones.data.data
          : [];
      setDirecciones(rawDirecciones.map((d) => Models.direcciones(d)));

      // Tipos unidad org
      const rawTipos = Array.isArray(resTipos.data)
        ? resTipos.data
        : Array.isArray(resTipos.data?.data)
          ? resTipos.data.data
          : [];
      setTiposUnidadOrg(rawTipos.map((t) => Models.tipoUnidadOrg(t)));

      // Empresas
      setEmpresas((resEmpresas.data || []).map(Models.empresas));

      // Relaciones
      setEmpresasUnidadesOrg((resRel.data || []).map(Models.empresasUnidadesOrg));

    } catch (e) {
      console.error(e);
      triggerAlert('Ocurrió un error al cargar información. Inténtelo más tarde.');
    } finally {
      setLoading(false);
    }
  }, [showChildData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ✅ índices para búsquedas rápidas por ID
  const zonasIndex = useMemo(() => {
    const map = {};
    zonas.forEach((z) => {
      if (!z) return;

      // intentamos varias formas posibles de id
      const id =
        z.idZonaGeografica ??
        z.IdZonaGeografica ??
        z.idZona ??
        z.IdZona;

      if (id != null) {
        map[id] = z;
      }
    });
    return map;
  }, [zonas]);


  const direccionesIndex = useMemo(() => {
    const map = {};
    direcciones.forEach((d) => {
      if (d && d.idDireccion != null) {
        map[d.idDireccion] = d;
      }
    });
    return map;
  }, [direcciones]);

  const tiposUnidadOrgIndex = useMemo(() => {
    const map = {};
    tiposUnidadOrg.forEach((t) => {
      const id =
        t.idTipoUnidadOrganizacional ??
        t.idTipoUndiadOrganizacional ??
        t.idTipoUnidadOrg;
      if (id != null) map[id] = t;
    });
    return map;
  }, [tiposUnidadOrg]);

  const empresasIndex = useMemo(() => {
    const map = {};
    empresas.forEach((e) => {
      if (e && e.idEmpresa != null) map[e.idEmpresa] = e;
    });
    return map;
  }, [empresas]);

  const relEmpresaIndex = useMemo(() => {
    const map = {};
    empresasUnidadesOrg.forEach((rel) => {
      if (rel && rel.idUnidadOrganizacional != null) map[rel.idUnidadOrganizacional] = rel.idEmpresa;
    });
    return map;
  }, [empresasUnidadesOrg]);

  // 🧩 helpers de formato

  const formatEmpresa = useCallback((r) => {
    const idEmpresa = relEmpresaIndex[r.idUnidadOrganizacional];
    if (!idEmpresa) return 'Sin Empresa';
    const emp = empresasIndex[idEmpresa];
    return normalizeValue(emp ? emp.nombre : 'Not Found');
  }, [relEmpresaIndex, empresasIndex]);

  // 🔹 AHORA ZONA TAMBIÉN SALE EN TEXTO (clave - nombre)
  const formatZona = useCallback((r) => {
    // id de la zona en la fila de UnidadOrg (defensivo)
    const idZona =
      r.idZonaGeografica ??
      r.IdZonaGeografica ??
      r.idZona ??
      r.IdZona;

    const zona = zonasIndex[idZona];
    if (!zona) {
      return 'Not Found';
    }

    const clave =
      zona.clave ??
      zona.Clave ??
      zona.codigo ??
      zona.Codigo ??
      '';

    const nombre =
      zona.nombre ??
      zona.Nombre ??
      zona.descripcion ??
      zona.Descripcion ??
      '';

    const label =
      (clave && nombre
        ? `${clave} - ${nombre}`
        : nombre || clave) || `Zona ${idZona}`;

    return normalizeValue(label);
  }, [zonasIndex]);


  const formatDireccion = useCallback((r) => {
    const dir = direccionesIndex[r.idDireccion];
    if (!dir) return normalizeValue(r.idDireccion);

    const parts = [];

    const loc = [dir.estado, dir.municipio, dir.colonia].filter(Boolean).join(', ');
    if (loc) parts.push(loc);

    if (dir.calle) {
      let calle = dir.calle;
      if (dir.numeroExterior) calle += ` ${dir.numeroExterior}`;
      parts.push(calle);
    }

    return normalizeValue(parts.join(' · '));
  }, [direccionesIndex]);

  const formatTipoUnidadOrg = useCallback((r) => {
    const idTipo =
      r.idTipoUnidadOrganizacional ?? r.idTipoUndiadOrganizacional;
    const tipo = tiposUnidadOrgIndex[idTipo];
    if (!tipo) return 'Not Found';

    const label =
      (tipo.clave && tipo.nombre
        ? `${tipo.clave}`
        : tipo.nombre || tipo.clave) || `Tipo ${idTipo}`;
    return normalizeValue(label);
  }, [tiposUnidadOrgIndex]);

  const filtered = useMemo(() => {
    // El SP ya pre-filtra los datos según el tipo (propias/hijas),
    // aquí solo aplicamos el filtro de búsqueda por texto.
    const dataToFilter = rows;

    // 2. Filtro de búsqueda
    const q = query.trim().toLowerCase();
    const result = q 
      ? dataToFilter.filter((r) => {
          const clave = (r.clave || '').toLowerCase();
          const nombre = (r.nombre || '').toLowerCase();
          const zona = (formatZona(r) || '').toLowerCase();
          const dir = (formatDireccion(r) || '').toLowerCase();
          const tipo = (formatTipoUnidadOrg(r) || '').toLowerCase();
          const empresa = (formatEmpresa(r) || '').toLowerCase();
          return (
            clave.includes(q) ||
            nombre.includes(q) ||
            zona.includes(q) ||
            dir.includes(q) ||
            tipo.includes(q) ||
            empresa.includes(q)
          );
        })
      : dataToFilter;

    // 3. Ordenamiento Jerárquico
    return result.sort((a, b) => {
      const aIdEmp = relEmpresaIndex[a.idUnidadOrganizacional] || a.idEmpresa;
      const bIdEmp = relEmpresaIndex[b.idUnidadOrganizacional] || b.idEmpresa;

      const aIsOwn = Number(aIdEmp) === sessionEmpresaId;
      const bIsOwn = Number(bIdEmp) === sessionEmpresaId;

      if (aIsOwn && !bIsOwn) return -1;
      if (!aIsOwn && bIsOwn) return 1;

      // Por nombre de empresa
      const aNomEmp = formatEmpresa(a) || '';
      const bNomEmp = formatEmpresa(b) || '';
      const empComp = aNomEmp.localeCompare(bNomEmp);
      if (empComp !== 0) return empComp;

      // Por nombre de la unidad
      const aNom = a.nombre || '';
      const bNom = b.nombre || '';
      return aNom.localeCompare(bNom);
    });
  }, [rows, query, formatZona, formatDireccion, formatTipoUnidadOrg, formatEmpresa, relEmpresaIndex, showChildData, sessionEmpresaId, isSuperAdmin]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
  };

  const handleEdit = (id) => {
    const r = rows.find(x => x.idUnidadOrganizacional === id);
    if (!r) {
      triggerAlert('No se encontró la fila en memoria.');
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
  };

  const askDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      await api.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.message || 'Ocurrió un error al eliminar. Inténtelo más tarde.';
      triggerAlert(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);
      let targetId = selectedId;

      if (mode === 'new') {
        const res = await api.post(ENDPOINTS.unidadesOrg, maps.mapCreate(payload));
        targetId = res?.data?.idUnidadOrganizacional ?? (typeof res?.data === 'number' ? res.data : null);
      } else {
        await api.put(ENDPOINTS.unidadesOrg, maps.mapUpdate(selectedId, payload));
      }

      // Manejar relación con empresa
      if (targetId && payload.idEmpresa) {
        try {
          // Buscar si ya existe la relación
          const resRel = await api.get(ENDPOINTS.empresasUnidadesOrg);
          const allRels = (resRel.data || []).map(Models.empresasUnidadesOrg);
          const existing = allRels.find(r => r.idUnidadOrganizacional === targetId);

          const relPayload = {
            idEmpresa: payload.idEmpresa,
            idUnidadOrganizacional: targetId,
            activo: true
          };

          if (existing) {
            // Actualizar si cambió la empresa
            if (existing.idEmpresa !== payload.idEmpresa) {
              await api.put(ENDPOINTS.empresasUnidadesOrg, {
                ...relPayload,
                idEmpresasUnidadesOrg: existing.idEmpresasUnidadesOrg
              });
            }
          } else {
            // Crear nueva relación
            await api.post(ENDPOINTS.empresasUnidadesOrg, relPayload);
          }
        } catch (eRel) {
          console.error("Error updating relationship:", eRel);
        }
      }

      setModalOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.message || 'Ocurrió un error al guardar. Inténtelo más tarde.';
      triggerAlert(msg);
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`
      }
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button >
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por clave, nombre, zona, dirección o tipo…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />



          {!isSessionSuperAdmin() && (
            <button 
              className={`btn-action ${showChildData ? 'btn-action--active' : ''}`} 
              style={{ 
                backgroundColor: showChildData ? '#0f172a' : '#f1f5f9', 
                color: showChildData ? '#fff' : '#475569',
                border: '1px solid #cbd5e1',
                marginRight: '8px'
              }}
              onClick={() => { setShowChildData(!showChildData); setPage(1); }}
            >
              {showChildData ? '👁️ Ocultar Hijas' : '👁️ Ver Hijas'}
            </button>
          )}

          {puedeCrear && (
            <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
          )}
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Zona</th>
                  <th>Clave</th>
                  <th>Nombre</th>
                  <th>Tipo Unidad Org.</th>
                  <th>Empresa</th>
                  {puedeEditar && <th style={{ textAlign: 'center' }}>Editar</th>}
                  {puedeEliminar && <th style={{ textAlign: 'center' }}>Borrar</th>}
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => {
                  const idEmpresa = relEmpresaIndex[r.idUnidadOrganizacional] || r.idEmpresa;
                  const isOwn = isSessionSuperAdmin() || idEmpresa === getSessionEmpresaId();
                  
                  // REGLA: Bloqueado si es de una hija delegada CON empleados, 
                  // O si la UO específica tiene empleados (restricción absoluta de borrado/edición clave)
                  const isLockedByHierarchy = !isOwn && r.tieneEmpleados;
                  const isLockedByEmployees = r.tieneEmpleadosUO;

                  const hierarchyTitle = "Bloqueado: Empresa hija con empleados";
                  const employeesTitle = "Bloqueado: Esta UO tiene empleados asignados activos.";

                  return (
                    <tr key={r.idUnidadOrganizacional}>
                      <td>{formatZona(r)}</td>
                      <td><strong>{normalizeValue(r.clave)}</strong></td>
                      <td>{normalizeValue(r.nombre) || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>(sin nombre)</span>}</td>
                      <td>{formatTipoUnidadOrg(r)}</td>
                      <td>{formatEmpresa(r)}</td>
                      {puedeEditar && (
                        <td style={{ textAlign: 'center' }}>
                          {isLockedByHierarchy ? (
                            <span title={hierarchyTitle} style={{ cursor: 'not-allowed', opacity: 0.5 }}>
                              🔒
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="rk-iconbtn rk-iconbtn--edit"
                              onClick={() => handleEdit(r.idUnidadOrganizacional)}
                              aria-label="Editar"
                              title="Editar"
                            >
                              <img src={sigmaEdit} alt="" className="rk-ico" />
                            </button>
                          )}
                        </td>
                      )}
                      {puedeEliminar && (
                        <td style={{ textAlign: 'center' }}>
                          {isLockedByHierarchy ? (
                            <span title={hierarchyTitle} style={{ cursor: 'not-allowed', opacity: 0.5 }}>
                              🔒
                            </span>
                          ) : isLockedByEmployees ? (
                            <span title={employeesTitle} style={{ cursor: 'not-allowed', opacity: 0.3, display: 'inline-block' }}>
                              <img src={sigmaDelete} alt="Bloqueado" className="rk-ico" style={{ filter: 'grayscale(100%)' }} />
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="rk-iconbtn rk-iconbtn--del"
                              onClick={() => {
                                askDelete(r.idUnidadOrganizacional, r.nombre || 'Unidad Org');
                              }}
                              aria-label="Borrar"
                              title="Borrar"
                            >
                              <img src={sigmaDelete} alt="" className="rk-ico" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
                 {!loading && paged.length === 0 && (
                   <tr><td colSpan={5 + (puedeEditar ? 1 : 0) + (puedeEliminar ? 1 : 0)}>Sin resultados.</td></tr>
                 )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0
              ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
              : <span>0 resultados</span>}
          </div>
          <div className="roles-pagination-right">
            <PageBtn
              label="‹"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            />
            {pageNumbers.map(n => (
              <PageBtn
                key={n}
                label={n}
                onClick={() => setPage(n)}
                active={n === page}
              />
            ))}
            <PageBtn
              label="›"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            />
          </div>
        </div>

        {modalOpen && (
          <div className="roles-editor-slot" ref={editorRef}>
            <Modal
              open={modalOpen}
              mode={modalMode}
              data={editData}
              inline
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
            />
             {modalOpen && modalMode === 'edit' && selectedId > 0 && (
               <Tabs 
                 unitId={selectedId} 
                 onUpdate={loadData}
                 puedeCrear={puedeCrear}
                 puedeEditar={puedeEditar}
                 puedeEliminar={puedeEliminar}
               />
             )}
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />

      <AlertDialog
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </div>
  );
}
